import { INPUT_LIMITS } from '../constants/input-limits';
import { LanguageEntry, normalizeToLanguageEntries } from '../constants/language-levels';
import { IAusbildung } from './ausbildung.model';
import { IFaehigkeit } from './faehigkeit.model';
import { isKnownNationality } from '../nationality-util';
import {
  isSecurityCheckExpired,
  isValidJahrgang,
} from './personal-data-validation';
import { IProjekt, isExportable } from './projekt.model';
import { ISchulung } from './schulung.model';
import { ISecurityCheck } from './personaldaten.model';
import { IWerdegang } from './werdegang.model';
import { IZertifikat } from './zertifikat.model';
import { listProjectValidation } from './projekt-validation';
import { ValidationFinding, ValidationSection } from './validation-finding';

/**
 * NP20-1192: Das eine Regelwerk hinter den roten Badges. Beraterprofil und
 * Beraterübersicht lesen beide diesen Baustein — vorher rechnete nur das Profil,
 * und eine zweite Rechnung in der Übersicht waere unweigerlich davon abgewichen.
 *
 * Die Regeln sind gegenueber ConsultantComponent unveraendert uebernommen; neu ist
 * allein die Zahl der geprueften Stellen je Abschnitt, aus der NP20-1190 den
 * Prozentwert ableitet.
 *
 * Bewusst ohne Angular: keine Injectables, keine Services. Was von aussen kommt —
 * die bekannten Branchen- und Schwerpunkt-Namen und die Bedingung fuer das
 * Eintrittsdatum — wird als Parameter hereingereicht.
 */

const REQUIRED_REASON = 'general.errors.required';
const LIMITED_TRAVEL = 'Eingeschränkt';

/** Abschnitte, deren rote Badges in die Ampel eingehen. `tasks` zaehlt zu `projects`. */
export const COMPLETENESS_SECTIONS = [
  'profil',
  'personal',
  'education',
  'career',
  'trainings',
  'certificates',
  'projects',
  'skills',
] as const;

export type CompletenessSection = (typeof COMPLETENESS_SECTIONS)[number];

/**
 * Schmale Eingabe, die sowohl `Berater` (verschachtelt unter `personaldaten`) als auch
 * `IBeraterUebersicht` (flach) bedienen koennen. Die Uebersetzung liegt in
 * `fromBerater` / `fromBeraterUebersicht` — an genau einer Stelle.
 */
export interface CompletenessInput {
  /** `false`, wenn der Datensatz gar keine Personaldaten mitbringt: der Abschnitt zaehlt dann nicht. */
  hasPersonalData?: boolean;
  position?: string;
  beraterSeit?: string | Date;
  verfuegbar?: unknown;
  wohnort?: string;
  nationalitaet?: string;
  jahrgang?: string;
  reisebereitschaft?: string;
  einschraenkung?: string;
  hireDate?: Date | string;
  sprachen?: LanguageEntry[];
  branchen?: string[];
  schwerpunkte?: string[];
  securityChecks?: ISecurityCheck[];
  ausbildung?: IAusbildung[];
  werdegang?: IWerdegang[];
  schulungen?: ISchulung[];
  zertifikate?: IZertifikat[];
  projekte?: IProjekt[];
  faehigkeiten?: IFaehigkeit[];
  /** NP20-1231: Profilbild-IDs; fehlt oder leer → 1 Badge an Abschnitt „Profil". */
  photoIds?: string[];
  /** NP20-1231: Steckbrief-Text; fehlt, null oder < INTRO_MIN Zeichen → 1 Badge an Abschnitt „Profil". */
  intro?: string | null;
  /**
   * NP20-1231 Graceful Degradation: Wenn `true`, entfällt der Profil-Abschnitt
   * komplett aus der Berechnung (0 Stellen, 0 Badges). Wird von
   * `fromBeraterUebersicht` gesetzt, wenn `intro === undefined` (BE liefert das
   * Feld noch nicht). Im Profil-Kontext (`fromBerater`) niemals gesetzt.
   */
  omitProfilSection?: boolean;
}

/** Rollen, fuer die das Eintrittsdatum ein Pflichtfeld ist. */
export const HIRE_DATE_ROLES = ['Office', 'Sale', 'Admin'];

/** Die beiden Groessen, die von aussen kommen und deshalb nicht fest verdrahtet sind. */
export interface CompletenessOptions {
  /** Namen aus dem aktuellen Branchen-Katalog; leer heisst "noch nicht geladen". */
  knownIndustryNames?: Set<string>;
  /** Namen aus dem aktuellen Schwerpunkt-Katalog; leer heisst "noch nicht geladen". */
  knownMainFocusNames?: Set<string>;
  /** Heute: Rolle Office, Sale oder Admin UND der Berater ist kein Bewerber. */
  hireDateRequired?: boolean;
}

export interface SectionCompleteness {
  findings: ValidationFinding[];
  /** Die Badge-Zahl des Abschnitts — nicht zwingend `findings.length`. */
  badges: number;
  /** Zahl der Stellen, die die Pruefung in diesem Abschnitt betrachtet hat. */
  checked: number;
}

export interface ProfileCompleteness {
  sections: Record<CompletenessSection, SectionCompleteness>;
  /** Summe der sieben Abschnitts-Badges. */
  totalBadges: number;
  /** Summe aller Fundstellen — feingranularer als `totalBadges` und Basis des Prozentwerts. */
  totalFindings: number;
  totalChecked: number;
  /** Ganzzahlig, 0–100. Ohne gepruefte Stellen 100. */
  percent: number;
}

/** Stufen der Ampel: gruen bei 0 Badges, orange bei 1–5, rot ab 6. */
export type CompletenessLevel = 'complete' | 'few' | 'many';

const FEW_MAX_BADGES = 5;

export function completenessLevel(badges: number): CompletenessLevel {
  if (badges <= 0) return 'complete';
  return badges <= FEW_MAX_BADGES ? 'few' : 'many';
}

export function levelLabelKey(level: CompletenessLevel): string {
  return `userlist.completeness.level.${level}`;
}

export function sectionLabelKey(section: CompletenessSection): string {
  return `userlist.completeness.section.${section}`;
}

/**
 * Fundstellen, Badge-Zahlen, geprüfte Stellen und Prozentwert eines Beraters.
 *
 * Die Intro-Fundstelle bleibt bewusst aussen vor: sie gehoert schon vor NP20-1192
 * nicht zur Badge-Zahl.
 */
export function computeProfileCompleteness(
  input: CompletenessInput | null | undefined,
  options: CompletenessOptions = {},
): ProfileCompleteness {
  const value = input ?? {};
  const education = listEducationFindings(value);
  const career = listCareerFindings(value);
  const trainings = listTrainingFindings(value);
  const certificates = listCertificateFindings(value);
  const skills = listSkillFindings(value);
  const projects = projectSection(value);

  const sections: Record<CompletenessSection, SectionCompleteness> = {
    profil: profilSection(value),
    personal: byFinding(
      listPersonalDataFindings(value, options),
      personalCheckedCount(value, options),
    ),
    education: byEntry(
      education,
      EDUCATION_FIELDS.length * (value.ausbildung?.length ?? 0),
    ),
    career: careerSection(value, career),
    trainings: byEntry(
      trainings,
      TRAINING_FIELDS.length * (value.schulungen?.length ?? 0),
    ),
    certificates: byFinding(certificates, value.zertifikate?.length ?? 0),
    projects,
    skills: byFinding(skills, value.faehigkeiten?.length ?? 0),
  };

  const totals = COMPLETENESS_SECTIONS.reduce(
    (acc, section) => ({
      badges: acc.badges + sections[section].badges,
      findings: acc.findings + sections[section].findings.length,
      checked: acc.checked + sections[section].checked,
    }),
    { badges: 0, findings: 0, checked: 0 },
  );

  return {
    sections,
    totalBadges: totals.badges,
    totalFindings: totals.findings,
    totalChecked: totals.checked,
    percent: completenessPercent(totals.findings, totals.checked),
  };
}

/**
 * NP20-1190: `round(100 × (1 − min(Fundstellen, geprüfte Stellen) / geprüfte Stellen))`.
 *
 * Die Deckelung ist noetig, weil einzelne Pruefungen mehr als eine Fundstelle je Stelle
 * liefern koennen (je veralteter Branche eine) — ohne sie wuerde der Wert negativ.
 */
export function completenessPercent(findings: number, checked: number): number {
  if (checked <= 0) return 100;
  const capped = Math.min(Math.max(findings, 0), checked);
  return Math.round(100 * (1 - capped / checked));
}

/** Uebersetzung der verschachtelten Profilform auf die Eingabe des Bausteins. */
export function fromBerater(
  berater:
    | {
        personaldaten?: CompletenessInput & {
          securityCheck?: ISecurityCheck[];
          photos?: { photoId?: string }[];
        };
        ausbildung?: IAusbildung[];
        werdegang?: IWerdegang[];
        schulungen?: ISchulung[];
        zertifikate?: IZertifikat[];
        projekte?: IProjekt[];
        faehigkeiten?: IFaehigkeit[];
      }
    | null
    | undefined,
): CompletenessInput {
  const personal = berater?.personaldaten;
  return {
    hasPersonalData: !!personal,
    position: personal?.position,
    beraterSeit: personal?.beraterSeit,
    verfuegbar: personal?.verfuegbar,
    wohnort: personal?.wohnort,
    nationalitaet: personal?.nationalitaet,
    jahrgang: personal?.jahrgang,
    reisebereitschaft: personal?.reisebereitschaft,
    einschraenkung: personal?.einschraenkung,
    hireDate: personal?.hireDate,
    sprachen: normalizeToLanguageEntries(personal?.sprachen as LanguageEntry[] | string[] | undefined),
    branchen: personal?.branchen,
    schwerpunkte: personal?.schwerpunkte,
    securityChecks: personal?.securityCheck,
    intro: personal?.intro,
    ausbildung: berater?.ausbildung,
    werdegang: berater?.werdegang,
    schulungen: berater?.schulungen,
    zertifikate: berater?.zertifikate,
    // NP20-1057: ein nie freigegebener Entwurf ist noch kein Teil des Profils
    projekte: berater?.projekte?.filter(isExportable),
    faehigkeiten: berater?.faehigkeiten,
    // NP20-1231: Foto-IDs aus personaldaten.photos für die Profil-Vollständigkeitsprüfung
    photoIds: (personal?.photos ?? []).map(p => p.photoId).filter((id): id is string => !!id),
  };
}

/**
 * Uebersetzung der flachen Uebersichtsform auf die Eingabe des Bausteins.
 *
 * `null`, wenn die Antwort die Begruendung zur eingeschraenkten Reisebereitschaft gar
 * nicht mitliefert (Stand vor NP20-1191): dann ist die Badge-Zahl nicht bestimmbar und
 * die Ampel wird ausgelassen statt geraten. Ein geliefertes leeres Feld ist etwas
 * anderes — es zaehlt wie im Profil als fehlend.
 */
export function fromBeraterUebersicht(
  uebersicht: CompletenessInput | null | undefined,
): CompletenessInput | null {
  if (!uebersicht) return null;
  if (
    uebersicht.reisebereitschaft === LIMITED_TRAVEL &&
    uebersicht.einschraenkung === undefined
  ) {
    return null;
  }
  // NP20-1231: Graceful Degradation für intro — wenn BE das Feld noch nicht
  // liefert (undefined), Profil-Abschnitt aus der Berechnung weglassen.
  // Die Ampel bleibt sichtbar, zeigt aber keinen Profil-Beitrag.
  const introMissing = uebersicht.intro === undefined;
  return {
    hasPersonalData: uebersicht.hasPersonalData,
    position: uebersicht.position,
    beraterSeit: uebersicht.beraterSeit,
    verfuegbar: uebersicht.verfuegbar,
    wohnort: uebersicht.wohnort,
    nationalitaet: uebersicht.nationalitaet,
    jahrgang: uebersicht.jahrgang,
    reisebereitschaft: uebersicht.reisebereitschaft,
    einschraenkung: uebersicht.einschraenkung,
    hireDate: uebersicht.hireDate,
    sprachen: normalizeToLanguageEntries(uebersicht.sprachen as LanguageEntry[] | string[] | undefined),
    branchen: uebersicht.branchen,
    schwerpunkte: uebersicht.schwerpunkte,
    securityChecks: uebersicht.securityChecks,
    ausbildung: uebersicht.ausbildung,
    werdegang: uebersicht.werdegang,
    schulungen: uebersicht.schulungen,
    zertifikate: uebersicht.zertifikate,
    projekte: uebersicht.projekte,
    faehigkeiten: uebersicht.faehigkeiten,
    photoIds: uebersicht.photoIds,
    intro: uebersicht.intro,
    ...(introMissing ? { omitProfilSection: true } : {}),
  };
}

function byFinding(
  findings: ValidationFinding[],
  checked: number,
): SectionCompleteness {
  return { findings, badges: findings.length, checked };
}

/**
 * NP20-1093: Die Badges von Ausbildung, Werdegang und Schulungen zaehlen unvollstaendige
 * *Eintraege*, nicht einzelne Felder. Die Fundstellen liegen feldgenau vor (das Warn-Icon
 * haengt am einzelnen Feld), die Badge-Zahl bleibt die Anzahl der betroffenen Eintraege —
 * abgeleitet aus derselben Liste, damit es keinen zweiten Rechenweg gibt.
 */
function byEntry(
  findings: ValidationFinding[],
  checked: number,
): SectionCompleteness {
  return { findings, badges: countAffectedEntries(findings), checked };
}

export function countAffectedEntries(findings: ValidationFinding[]): number {
  return new Set(findings.map(({ entryIndex }) => entryIndex)).size;
}

/** Pflichtfeld-Fundstellen eines Listeneintrags (Ausbildung, Werdegang, Schulungen). */
function requiredFindings(
  section: ValidationSection,
  entryIndex: number,
  fields: { field: string; labelKey: string; missing: boolean }[],
): ValidationFinding[] {
  return fields
    .filter(({ missing }) => missing)
    .map(({ field, labelKey }) => ({
      section,
      field,
      labelKey,
      reasonKey: REQUIRED_REASON,
      entryIndex,
    }));
}

export function listCertificateFindings(
  value: CompletenessInput,
): ValidationFinding[] {
  return (value.zertifikate ?? []).flatMap((zertifikat, entryIndex) =>
    zertifikat.uploadId
      ? []
      : [
          {
            section: 'certificates' as ValidationSection,
            field: 'uploadId',
            labelKey: 'certificates.type',
            reasonKey: 'certificates.noCertificateUploaded',
            entryIndex,
          },
        ],
  );
}

export function listSkillFindings(
  value: CompletenessInput,
): ValidationFinding[] {
  return (value.faehigkeiten ?? []).flatMap((skill, entryIndex) => {
    const level = skill.level;
    const missing =
      !level || level.trim().length === 0 || level === 'NICHT_VORHANDEN';
    return missing
      ? [
          {
            section: 'skills' as ValidationSection,
            field: 'level',
            labelKey: 'skills.level',
            reasonKey: REQUIRED_REASON,
            entryIndex,
          },
        ]
      : [];
  });
}

interface RequiredField<T> {
  field: string;
  labelKey: string;
  missing: (entry: T | undefined) => boolean;
}

const EDUCATION_FIELDS: RequiredField<IAusbildung>[] = [
  { field: 'beginn', labelKey: 'education.from', missing: (e) => !e?.beginn },
  {
    field: 'ausbildungseinrichtung',
    labelKey: 'education.trainingInstitution',
    missing: (e) => !e?.ausbildungseinrichtung?.trim(),
  },
  {
    field: 'ausbildungsgang',
    labelKey: 'education.trainingCourse',
    missing: (e) => !e?.ausbildungsgang?.trim(),
  },
  {
    field: 'abschluss',
    labelKey: 'education.diploma',
    missing: (e) => !e?.abschluss?.trim(),
  },
  {
    field: 'ort',
    labelKey: 'education.location',
    missing: (e) => !e?.ort?.trim(),
  },
];

const CAREER_FIELDS: RequiredField<IWerdegang>[] = [
  { field: 'beginn', labelKey: 'career.beginning', missing: (e) => !e?.beginn },
  {
    field: 'unternehmen',
    labelKey: 'career.company',
    missing: (e) => !e?.unternehmen?.trim(),
  },
  {
    field: 'stellenbezeichnung',
    labelKey: 'career.jobTitle',
    missing: (e) => !e?.stellenbezeichnung?.trim(),
  },
  { field: 'ort', labelKey: 'career.location', missing: (e) => !e?.ort?.trim() },
  {
    field: 'taetigkeit',
    labelKey: 'career.activity',
    missing: (e) => !e?.taetigkeit?.trim(),
  },
];

const TRAINING_FIELDS: RequiredField<ISchulung>[] = [
  {
    field: 'beginn',
    labelKey: 'trainings.timePeriod',
    missing: (e) => !e?.beginn,
  },
  {
    field: 'schulungsanbieter',
    labelKey: 'trainings.trainingProvider',
    missing: (e) => !e?.schulungsanbieter?.trim(),
  },
  {
    field: 'lehrgang',
    labelKey: 'trainings.trainingCourse',
    missing: (e) => !e?.lehrgang?.trim(),
  },
];

function entryFindings<T>(
  section: ValidationSection,
  entries: T[] | undefined,
  fields: RequiredField<T>[],
): ValidationFinding[] {
  return (entries ?? []).flatMap((entry, entryIndex) =>
    requiredFindings(
      section,
      entryIndex,
      fields.map(({ field, labelKey, missing }) => ({
        field,
        labelKey,
        missing: missing(entry),
      })),
    ),
  );
}

export function listEducationFindings(
  value: CompletenessInput,
): ValidationFinding[] {
  return entryFindings('education', value.ausbildung, EDUCATION_FIELDS);
}

export function listCareerFindings(
  value: CompletenessInput,
): ValidationFinding[] {
  return entryFindings('career', value.werdegang, CAREER_FIELDS);
}

export function listTrainingFindings(
  value: CompletenessInput,
): ValidationFinding[] {
  return entryFindings('trainings', value.schulungen, TRAINING_FIELDS);
}

/**
 * NP20-828: Fehlende Pflichtfelder in Persoenliche-Daten. Die Feld-Liste spiegelt die
 * Validators.required in PersonalDataComponent.personalForm. Das Eintrittsdatum ist nur
 * pflicht, wenn der aktuelle Benutzer Office/Sale/Admin ist UND der Berater kein
 * Applicant — die Bedingung kommt als `hireDateRequired` von aussen.
 */
export function listPersonalDataFindings(
  value: CompletenessInput,
  options: CompletenessOptions = {},
): ValidationFinding[] {
  const stringMissing = (v: unknown): boolean =>
    typeof v !== 'string' || v.trim().length === 0;
  const arrayMissing = (v: unknown): boolean =>
    !Array.isArray(v) || v.length === 0;
  const findings: ValidationFinding[] = [];
  const add = (
    field: string,
    labelKey: string,
    reasonKey: string,
    reasonParams?: Record<string, unknown>,
  ): void => {
    findings.push({
      section: 'personal',
      field,
      labelKey,
      reasonKey,
      ...(reasonParams ? { reasonParams } : {}),
    });
  };

  if (stringMissing(value.position))
    add('position', 'personalData.position', REQUIRED_REASON);
  if (!value.beraterSeit)
    add('beraterSeit', 'personalData.consultantSince', REQUIRED_REASON);
  if (!value.verfuegbar)
    add('verfuegbar', 'personalData.availableFrom', REQUIRED_REASON);
  if (stringMissing(value.wohnort))
    add('wohnort', 'personalData.placeOfResidence', REQUIRED_REASON);
  // Legacy-Nationalitaeten (z.B. Freitext-Adjektive vor der ISO-Migration) zaehlen
  // ebenfalls als fehlend, sonst faellt es nur beim Editieren auf.
  if (!isKnownNationality(value.nationalitaet)) {
    add(
      'nationalitaet',
      'personalData.nationality',
      stringMissing(value.nationalitaet)
        ? REQUIRED_REASON
        : 'personalData.error.legacyNationality',
    );
  }
  // Der Formular-Validator parst mit parseJahrgang() (DD.MM.YYYY oder YYYY) und
  // verwirft alles andere als null → Zaehler muss dieselbe Semantik haben, damit
  // das Badge nicht 0 zeigt, waehrend save() still abbricht (Reviewer PR #617).
  if (!isValidJahrgang(value.jahrgang)) {
    add(
      'jahrgang',
      'personalData.year',
      value.jahrgang ? 'personalData.validYear' : REQUIRED_REASON,
    );
  }
  if (stringMissing(value.reisebereitschaft)) {
    add(
      'reisebereitschaft',
      'personalData.willingnessToTravel',
      REQUIRED_REASON,
    );
  }
  if (arrayMissing(value.sprachen)) {
    add(
      'sprachen',
      'personalData.languages',
      'personalData.error.languagesRequired',
    );
  } else {
    (value.sprachen ?? [])
      .filter((entry) => !entry.level)
      .forEach(() => {
        add(
          'sprachen',
          'personalData.languages',
          'personalData.languageLevels.missingLevel',
        );
      });
  }
  if (arrayMissing(value.branchen)) {
    add(
      'branchen',
      'personalData.industries',
      'personalData.error.industriesRequired',
    );
  }
  if (arrayMissing(value.schwerpunkte)) {
    add(
      'schwerpunkte',
      'personalData.mainFocus',
      'personalData.error.mainFocusRequired',
    );
  }
  // Legacy-Werte (Branchen/Schwerpunkte, die es nicht mehr im aktuellen Katalog gibt)
  // zaehlen jeweils einzeln — sonst sieht der Nutzer das erst beim Editieren.
  legacyEntries(value.branchen, options.knownIndustryNames).forEach((name) =>
    add(
      'branchen',
      'personalData.industries',
      'personalData.error.legacyIndustryValue',
      { value: name },
    ),
  );
  legacyEntries(value.schwerpunkte, options.knownMainFocusNames).forEach(
    (name) =>
      add(
        'schwerpunkte',
        'personalData.mainFocus',
        'personalData.error.legacyMainFocusValue',
        { value: name },
      ),
  );
  // Formular nutzt minLengthTrimmed(LIMITED_REASON_MIN); der Zaehler muss dieselbe
  // Schwelle setzen, sonst geht das Badge auf 0 bei "zu kurzer" Begruendung, obwohl
  // der Save-Button noch disabled ist.
  if (
    value.reisebereitschaft === LIMITED_TRAVEL &&
    (value.einschraenkung ?? '').trim().length < INPUT_LIMITS.LIMITED_REASON_MIN
  ) {
    add(
      'einschraenkung',
      'personalData.limited',
      (value.einschraenkung ?? '').trim().length === 0
        ? 'personalData.error.limitedReasonRequired'
        : 'personalData.error.limitedReasonMinLength',
      { min: INPUT_LIMITS.LIMITED_REASON_MIN },
    );
  }
  if (options.hireDateRequired && !value.hireDate) {
    add('hireDate', 'personalData.hireDate', REQUIRED_REASON);
  }
  // Aktive (nicht abgelaufene) SecurityChecks brauchen submittedOn — spiegelt
  // die Logik aus PersonalDataComponent.createSecurityCheckFormGroup.
  (value.securityChecks ?? []).forEach((sc, entryIndex) => {
    if (isSecurityCheckExpired(sc.completedOn) || sc.submittedOn) return;
    findings.push({
      section: 'personal',
      field: 'securityCheckSubmittedOn',
      labelKey: 'personalData.security.submittedDatePicker',
      reasonKey: REQUIRED_REASON,
      entryIndex,
    });
  });
  return findings;
}

function legacyEntries(
  stored: string[] | undefined,
  known: Set<string> | undefined,
): string[] {
  // Solange der Katalog nicht geladen ist (Set leer), nehmen wir defensiv keinen
  // entfallenen Wert an, um keine falschen Fehler zu melden.
  if (!stored?.length || !known?.size) return [];
  return stored.filter((name) => !known.has(name));
}

/**
 * NP20-1190: zehn feste Pflichtangaben, plus die Begruendung bei "Eingeschränkt", plus
 * das Eintrittsdatum wenn es verlangt wird, plus je aktiver Sicherheitsueberpruefung eine.
 * Die veralteten Branchen und Schwerpunkte zaehlen nicht mit — sie haengen an Stellen,
 * die bereits als `branchen` und `schwerpunkte` gezaehlt sind.
 */
const FIXED_PERSONAL_CHECKS = 10;

function personalCheckedCount(
  value: CompletenessInput,
  options: CompletenessOptions,
): number {
  const limitedReason = value.reisebereitschaft === LIMITED_TRAVEL ? 1 : 0;
  const hireDate = options.hireDateRequired ? 1 : 0;
  const activeSecurityChecks = (value.securityChecks ?? []).filter(
    (sc) => !isSecurityCheckExpired(sc.completedOn),
  ).length;
  return (
    FIXED_PERSONAL_CHECKS + limitedReason + hireDate + activeSecurityChecks
  );
}

/**
 * NP20-828: Harte Fehler (severity=error) ueber alle Projekte. Die eigentliche
 * Pruef-Logik liegt in projekt-validation.ts, damit sie zwischen Profil-Abschnitt,
 * Projektkarten-Badge und Uebersicht nicht auseinanderlaufen kann. Von dort kommt auch
 * die Zahl der geprueften Stellen — so entsteht kein zweites Regelwerk.
 *
 * NP20-1231: Leere Projektliste → 1 Badge / 1 geprüfte Stelle (Pflichtfeld).
 */
function projectSection(value: CompletenessInput): SectionCompleteness {
  if (!value.projekte?.length) {
    return { findings: [], badges: 1, checked: 1 };
  }
  const findings: ValidationFinding[] = [];
  let checked = 0;
  value.projekte.forEach((projekt, entryIndex) => {
    const result = listProjectValidation(projekt);
    findings.push(
      ...result.findings.map((finding) => ({ ...finding, entryIndex })),
    );
    checked += result.checked;
  });
  return { findings, badges: findings.length, checked };
}

/**
 * NP20-1231: Werdegang-Abschnitt. Leere Liste → 1 Badge / 1 geprüfte Stelle.
 * Sonst bestehende Prüfung (Pflichtfelder je Eintrag).
 */
function careerSection(
  value: CompletenessInput,
  findings: ValidationFinding[],
): SectionCompleteness {
  if (!value.werdegang?.length) {
    return { findings: [], badges: 1, checked: 1 };
  }
  return byEntry(findings, CAREER_FIELDS.length * value.werdegang.length);
}

/**
 * NP20-1231: Abschnitt „Profil" — Foto und Summary.
 * Immer 2 geprüfte Stellen; je 1 Badge wenn photoIds leer/fehlt oder intro fehlt/< 500 Z.
 *
 * Graceful Degradation: Wenn `omitProfilSection === true` (gesetzt von
 * `fromBeraterUebersicht`, wenn das BE `intro` noch nicht liefert), entfällt der
 * Abschnitt aus der Berechnung (0 Stellen, 0 Badges) statt falsche Badges zu erzeugen.
 */
function profilSection(value: CompletenessInput): SectionCompleteness {
  if (value.omitProfilSection) {
    return { findings: [], badges: 0, checked: 0 };
  }
  const PROFIL_CHECKED = 2;
  let badges = 0;
  if (!value.photoIds?.length) badges += 1;
  if (!value.intro || value.intro.length < INPUT_LIMITS.INTRO_MIN) badges += 1;
  return { findings: [], badges, checked: PROFIL_CHECKED };
}
